import { useState, useEffect, useRef, useCallback } from "react";

const STORAGE_KEY = "mock1-tutorial-complete";

// How long a hint's target may be absent before the hint clears itself.
// waitForDOM steps are documented to wait on a phase transition, so they get a
// longer budget rather than the same one.
const MEASURE_MS = 200;           // poll interval
const MEASURE_FAST_MS = 3000;
const MEASURE_SLOW_MS = 10000;    // waitForDOM steps

const HINT_SEQUENCE = [
  "opponents", "view-toggle", "watchlist", "roster", "tier-sheet",
  "nominate-player", "set-price", "place-bid",
  "auto-controls", "speed", "go",
];

// DOM selector (first match wins) + banner text + optional dismiss button
const HINT_CONFIG = {
  "opponents":       { selector: ".mock-scoreboard, .mock-scoreboard-mobile", label: "Track every team\u2019s budget and max bid (budget minus $1 per open slot). Click any team for their roster, or tap View All Rosters for the full draft board.", dismiss: "Got it", block: true },
  "view-toggle":     { selector: ".mob-panel-tabs", label: "Switch between Players and Tiers to see value context while bidding.", dismiss: "Got it", block: true },
  "watchlist":       { selector: ".mock-player-list", label: "Star players you\u2019re targeting. You\u2019ll get notified when they come up for auction so you never miss your guys.", dismiss: "Got it", block: true },
  "roster":          { selector: ".mock-roster-panel", label: "Your picks fill in here as you win auctions. Track your budget, open slots, and the draft log below.", dismiss: "Got it", block: true },
  "tier-sheet":      { selector: ".mock-tier-viewer-desktop", label: "Your tiers from the Tier Builder show here \u2014 use them to gauge value during bidding.", dismiss: "Got it", block: true },
  "nominate-player": { selector: ".mock-panel-inner", label: "Choose a player to put up for auction" },
  "set-price":       { selector: ".mock-nominating-panel, .mock-nomination-bar", label: "Set your opening bid and hit Nominate" },
  "place-bid":       { selector: ".mock-bid-buttons", label: "Bid to raise the price, or Pass to fast-forward through the room's bidding", dismiss: "Got it", waitForDOM: true },
  "auto-controls":   { selector: ".mock-main-controls, .mob-card-actions", label: "Auto-Bid bids for you. Skip to My Pick fast-forwards to your next nomination. Simulate to End runs the entire draft.", dismiss: "Got it", waitForDOM: true, block: true },
  "speed":           { selector: ".mock-card-controls", label: "Speed up, slow down, or pause at any time.", dismiss: "Got it", waitForDOM: true, block: true },
  "go":              { selector: null, label: "Got it? Now go get your guys!", dismiss: "Let's go" },
};

/**
 * GuidedTutorial — renders a dark overlay with spotlight cutout for the active hint.
 *
 * Props:
 *   currentHint  — string hint key or null
 *   advanceHint  — fn(from) to advance past a hint
 *   active       — whether to render (e.g. only during active draft phases)
 *   onSkip       — fn() called when the user hits the always-visible "Skip tutorial" button
 */
export default function GuidedTutorial({ currentHint, advanceHint, active, onSkip }) {
  const [rect, setRect] = useState(null);
  const elRef = useRef(null);

  // Find and measure the target element
  const measure = useCallback(() => {
    const config = HINT_CONFIG[currentHint];
    if (!config || !config.selector) { setRect(null); return; }
    const selectors = config.selector.split(",").map(s => s.trim());
    let el = null;
    for (const sel of selectors) {
      const candidate = document.querySelector(sel);
      if (candidate) {
        const cr = candidate.getBoundingClientRect();
        if (cr.width > 0 && cr.height > 0) { el = candidate; break; }
      }
    }
    elRef.current = el;
    if (!el) { console.warn(`[Tutorial] hint "${currentHint}" — no visible element found for "${config.selector}"`); setRect(null); return; }
    const r = el.getBoundingClientRect();
    console.log(`[Tutorial] hint "${currentHint}" — measured at`, { top: r.top, left: r.left, width: r.width, height: r.height });
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, [currentHint]);

  // Held in a ref so the measure effect below does not depend on onSkip's
  // identity — the parent passes an inline arrow, which would otherwise restart
  // the retry loop on every render and never let it reach its budget.
  const onSkipRef = useRef(onSkip);
  useEffect(() => { onSkipRef.current = onSkip; });

  // Initial measure after layout settles.
  //
  // FAIL OPEN. A hint that cannot be drawn must CLEAR itself rather than sit
  // invisible. tutorialActive (AuctionDraftBuilder) is derived from
  // tutorialHint alone and feeds loopIdle, which gates the entire auction loop
  // — so a hint that renders nothing while staying set strands the draft with
  // no UI left to clear it, because the Skip button is inside the markup that
  // did not render. "Target absent" is routine, not exotic: several selectors
  // are layout-specific (.mock-tier-viewer-desktop, .mob-panel-tabs), so the
  // wrong form factor alone is enough to trigger it.
  //
  // Clearing here deliberately does NOT write STORAGE_KEY. A transient layout
  // miss should cost this draft's tutorial, not the user's tutorial forever;
  // handleSkip still persists, because that one is a real choice.
  useEffect(() => {
    if (!active || !currentHint) { setRect(null); elRef.current = null; return; }
    const config = HINT_CONFIG[currentHint];
    // Unknown hint key — unrecoverable, and the render path below would return
    // null on every pass. No retry can help.
    if (!config) { onSkipRef.current?.(); return; }
    if (!config.selector) { setRect(null); return; }

    // A free-running INTERVAL, not a render-driven retry. The previous two
    // attempts both failed for the same reason: they re-evaluated only when
    // this component re-rendered, and a stranded loop may not be rendering —
    // a guard that depends on the thing it is rescuing cannot rescue it. This
    // polls the DOM directly on a timer, so it fires whether or not React,
    // the auction loop, or anything else is still alive.
    //
    // Covers both failure shapes with one mechanism:
    //   never measurable  — lastSeen never advances, clears after the budget
    //   measure-then-vanish — lastSeen stops advancing when the target goes,
    //     clears a budget later. A live browser run caught this one:
    //     place-bid measured cleanly, then .mock-bid-buttons disappeared 90s
    //     later when the auction card swapped its controls.
    const budgetMs = config.waitForDOM ? MEASURE_SLOW_MS : MEASURE_FAST_MS;
    const selectors = config.selector.split(",").map(s => s.trim());
    let lastSeen = Date.now();
    const iv = setInterval(() => {
      const visible = selectors.some(sel => {
        const el = document.querySelector(sel);
        return el && el.getBoundingClientRect().width > 0;
      });
      if (visible) {
        lastSeen = Date.now();
        if (!elRef.current) measure();        // (re)establish a rect on appearance
        return;
      }
      // Target gone: drop the stale cutout so we never draw against it.
      if (elRef.current) { elRef.current = null; setRect(null); }
      if (Date.now() - lastSeen >= budgetMs) {
        clearInterval(iv);
        onSkipRef.current?.();
      }
    }, MEASURE_MS);
    return () => clearInterval(iv);
  }, [active, currentHint, measure]);

  // Re-measure on scroll so cutout stays locked to viewport
  useEffect(() => {
    if (!active || !currentHint) return;
    const config = HINT_CONFIG[currentHint];
    if (!config || !config.selector) return;

    const onScroll = () => measure();
    window.addEventListener("scroll", onScroll, true);
    return () => window.removeEventListener("scroll", onScroll, true);
  }, [active, currentHint, measure]);

  if (!active || !currentHint) return null;

  const config = HINT_CONFIG[currentHint];
  if (!config) return null;

  // 920 to match the mock draft's narrow-layout boundary (styles/auction.css,
  // hooks/useMockDraft). This tutorial runs OVER a live draft and positions its
  // cutouts against whatever layout is on screen, so a threshold that disagrees
  // with that boundary aims desktop-style callouts at the stacked layout, or
  // vice versa. Above 920 the draft is three columns, which is the desktop
  // arrangement this component's non-mobile branch was written for.
  const isMobile = window.innerWidth <= 920;

  // Always-visible "Skip tutorial" escape hatch (top-right, every step)
  const handleSkip = () => {
    try { localStorage.setItem(STORAGE_KEY, "true"); } catch {}
    if (onSkip) onSkip();
  };
  const skipButton = (
    <button onClick={handleSkip} style={{
      position: "fixed",
      top: isMobile ? 8 : 10,
      right: isMobile ? 8 : 10,
      zIndex: 10001,
      pointerEvents: "auto",
      background: "rgba(0,0,0,0.45)",
      color: "#fff",
      fontSize: 12,
      fontWeight: 700,
      fontFamily: "'Exo 2', sans-serif",
      padding: isMobile ? "8px 12px" : "6px 12px",
      minHeight: isMobile ? 40 : undefined,
      borderRadius: 4,
      border: "1px solid rgba(255,255,255,0.35)",
      cursor: "pointer",
      whiteSpace: "nowrap",
    }}>Skip tutorial</button>
  );

  // Shared dismiss button style
  const dismissStyle = {
    background: "rgba(0,0,0,0.25)",
    color: "#fff",
    fontSize: isMobile ? 14 : 12,
    fontWeight: 800,
    fontFamily: "'Exo 2', sans-serif",
    padding: isMobile ? "10px 20px" : "4px 12px",
    minHeight: isMobile ? 44 : undefined,
    borderRadius: isMobile ? 6 : 4,
    border: "1px solid rgba(255,255,255,0.3)",
    cursor: "pointer",
    whiteSpace: "nowrap",
  };

  // "go" step — no spotlight, centered banner only
  if (!config.selector) {
    return (
      <div style={{ position: "fixed", inset: 0, zIndex: 9998, display: "flex", alignItems: isMobile ? "flex-end" : "center", justifyContent: "center", padding: isMobile ? "0 16px 20px" : 0 }}>
        <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.75)" }} />
        {skipButton}
        <div style={{ position: "relative", pointerEvents: "auto", textAlign: "center", maxWidth: "calc(100vw - 40px)", width: isMobile ? "100%" : undefined }}>
          <div style={{
            background: "#FF6B00",
            color: "#fff",
            fontSize: isMobile ? 18 : 22,
            fontWeight: 900,
            fontFamily: "'Exo 2', sans-serif",
            padding: isMobile ? "16px 20px" : "16px 32px",
            borderRadius: 10,
            boxShadow: "0 4px 30px rgba(255,107,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 16,
            flexWrap: "wrap",
          }}>
            {config.label}
            <button onClick={() => advanceHint(currentHint)} style={{
              ...dismissStyle,
              background: "#fff",
              color: "#FF6B00",
              border: "none",
              fontSize: 14,
              padding: "6px 18px",
              minHeight: isMobile ? 44 : undefined,
            }}>{config.dismiss}</button>
          </div>
        </div>
      </div>
    );
  }

  // Wait for measurement
  if (!rect) return null;

  const pad = 6;
  const cutout = {
    top: rect.top - pad,
    left: rect.left - pad,
    width: rect.width + pad * 2,
    height: rect.height + pad * 2,
  };

  // Banner positioning: mobile = fixed bottom toast, desktop = above cutout with arrow
  const bannerLeft = isMobile ? 16 : Math.max(8, Math.min(cutout.left, window.innerWidth - 20));

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9998, pointerEvents: config.block ? "auto" : "none" }}>
      {skipButton}
      {/* Dark overlay with clip-path cutout */}
      <div style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.75)",
        clipPath: `polygon(
          0% 0%, 100% 0%, 100% 100%, 0% 100%, 0% 0%,
          ${cutout.left}px ${cutout.top}px,
          ${cutout.left}px ${cutout.top + cutout.height}px,
          ${cutout.left + cutout.width}px ${cutout.top + cutout.height}px,
          ${cutout.left + cutout.width}px ${cutout.top}px,
          ${cutout.left}px ${cutout.top}px
        )`,
      }} />

      {/* Cutout border glow */}
      <div style={{
        position: "fixed",
        top: cutout.top, left: cutout.left,
        width: cutout.width, height: cutout.height,
        border: "2px solid rgba(255,140,0,0.6)",
        borderRadius: 10,
        boxShadow: "0 0 20px rgba(255,140,0,0.3), inset 0 0 20px rgba(255,140,0,0.05)",
        pointerEvents: "none",
      }} />

      {/* Label banner */}
      <div style={isMobile ? {
        position: "fixed",
        bottom: 20,
        left: 16,
        right: 16,
        zIndex: 10000,
        pointerEvents: "auto",
      } : {
        position: "fixed",
        bottom: Math.max(8, window.innerHeight - cutout.top + 4),
        left: bannerLeft,
        maxWidth: "calc(100vw - 40px)",
        pointerEvents: "auto",
      }}>
        <div style={{
          background: "#FF6B00",
          color: "#fff",
          fontSize: isMobile ? 15 : 14,
          fontWeight: 700,
          fontFamily: "'Exo 2', sans-serif",
          padding: isMobile ? "12px 16px" : "8px 16px",
          borderRadius: isMobile ? 10 : 6,
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
          boxShadow: isMobile ? "0 -2px 20px rgba(0,0,0,0.4)" : "none",
        }}>
          {config.label}
          {config.dismiss && (
            <button onClick={() => advanceHint(currentHint)} style={dismissStyle}>{config.dismiss}</button>
          )}
        </div>
        {/* Downward-pointing arrow — desktop only */}
        {!isMobile && (
          <div style={{
            width: 0, height: 0,
            marginLeft: 24,
            borderLeft: "8px solid transparent",
            borderRight: "8px solid transparent",
            borderTop: "8px solid #FF6B00",
          }} />
        )}
      </div>
    </div>
  );
}

export { HINT_CONFIG };
